/**
 * Sentry initialization for the React frontend.
 *
 * MUST be imported as the FIRST statement in src/index.js so the
 * SDK's global error/unhandledrejection hooks are installed before
 * any other code runs.
 *
 * No-op when REACT_APP_SENTRY_DSN is empty (local dev / tests).
 */
import * as Sentry from "@sentry/react";

const dsn = process.env.REACT_APP_SENTRY_DSN || "";
const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || "development";
const tracesSampleRate = Number(
  process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.1",
);

// Keys we always redact if they show up inside event payloads.
const SENSITIVE_KEYS = new Set([
  "password",
  "passwd",
  "secret",
  "token",
  "id_token",
  "access_token",
  "refresh_token",
  "session_token",
  "authorization",
  "cookie",
  "api_key",
  "apikey",
  "x-api-key",
]);

const REDACTED = "[Filtered]";

function scrubMapping(obj) {
  if (Array.isArray(obj)) return obj.map(scrubMapping);
  if (obj && typeof obj === "object") {
    const out = Array.isArray(obj) ? [] : { ...obj };
    for (const k of Object.keys(out)) {
      if (typeof k === "string" && SENSITIVE_KEYS.has(k.toLowerCase())) {
        out[k] = REDACTED;
      } else {
        out[k] = scrubMapping(out[k]);
      }
    }
    return out;
  }
  return obj;
}

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    tracesSampleRate,
    sendDefaultPii: false,
    // Quiet down in dev: only forward in non-dev environments.
    enabled: environment !== "test",
    beforeSend(event) {
      try {
        if (event.request) {
          if (event.request.data) event.request.data = scrubMapping(event.request.data);
          if (event.request.headers) event.request.headers = scrubMapping(event.request.headers);
          if (event.request.cookies) event.request.cookies = scrubMapping(event.request.cookies);
        }
        if (event.extra) event.extra = scrubMapping(event.extra);
        if (event.contexts) event.contexts = scrubMapping(event.contexts);
        if (event.user && typeof event.user === "object") {
          // Keep id/username for impact analysis; drop email.
          delete event.user.email;
          delete event.user.ip_address;
        }
      } catch (e) {
        // never block event delivery on a scrub failure
        // eslint-disable-next-line no-console
        console.warn("Sentry beforeSend scrubber failed:", e);
      }
      return event;
    },
  });
}

export default Sentry;
